import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ShowCard from "@/components/base/ShowCard";
import { shows } from "@/mocks/tv/shows";
import { genres as allGenres, regions as allRegions, sortOptions } from "@/mocks/tv/filters";
import { filterShows, searchShows, sortShows } from "@/utils/tv";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const typeTabs = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tvshow", label: "电视剧" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") ?? "";
  const initialGenre = params.get("genre") ?? "";
  const initialRegion = params.get("region") ?? "";
  const initialSort = params.get("sort") ?? "latest";

  const [input, setInput] = useState(initialQ);
  const [keyword, setKeyword] = useState(initialQ);
  const [type, setType] = useState("all");
  const [sort, setSort] = useState(initialSort);
  const [genre, setGenre] = useState(initialGenre);
  const [region, setRegion] = useState(initialRegion);

  useDocumentMeta({
    title: keyword ? `${keyword} 搜索结果 - 绍宋影视` : "搜索影视 - 绍宋影视大全",
    description: `在绍宋影视大全中搜索"${keyword || "影视作品"}"，快速找到你感兴趣的电影和电视剧。`,
    keywords: `绍宋,影视搜索,${keyword || "电影,电视剧"}`,
  });

  useEffect(() => {
    setKeyword(initialQ);
    setInput(initialQ);
  }, [initialQ]);

  const results = useMemo(() => {
    let list = keyword ? searchShows(shows, keyword) : shows;
    if (type !== "all") list = list.filter((s) => s.type === type);
    list = filterShows(list, {
      genres: genre ? [genre] : [],
      regions: region ? [region] : [],
      years: [],
    });
    return sortShows(list, sort);
  }, [keyword, type, genre, region, sort]);

  const syncParams = (next: Record<string, string>) => {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => {
      if (value) updated.set(key, value);
      else updated.delete(key);
    });
    setParams(updated, { replace: true });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setKeyword(input.trim());
    syncParams({ q: input.trim() });
  };

  const hasCondition = Boolean(keyword || type !== "all" || genre || region);

  const resetAll = () => {
    setInput("");
    setKeyword("");
    setType("all");
    setSort("latest");
    setGenre("");
    setRegion("");
    setParams(new URLSearchParams(), { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 pt-16">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-700">搜索</span>
          </nav>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">搜索影视</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-7">
            输入影片名、导演或演员进行模糊搜索，支持按类型筛选与多种排序方式。
          </p>

          <form onSubmit={submit} className="mt-6 max-w-2xl">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                placeholder="搜索影片名、导演、演员…"
                aria-label="搜索影视"
                className="w-full h-12 pl-12 pr-28 text-sm rounded-md border border-background-200 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {typeTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setType(tab.value)}
                  className={`px-4 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    type === tab.value
                      ? "bg-background-50 text-primary-600 font-medium"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSort(option.value)}
                  className={`px-3.5 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option.value
                      ? "bg-background-50 text-primary-600 font-medium"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-400">快捷筛选：</span>
            <select
              value={genre}
              onChange={(e) => {
                setGenre(e.target.value);
                syncParams({ genre: e.target.value });
              }}
              aria-label="按类型筛选"
              className="h-9 px-3 text-xs rounded-md border border-background-200 bg-background-50 text-foreground-700 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="">全部类型</option>
              {allGenres.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <select
              value={region}
              onChange={(e) => {
                setRegion(e.target.value);
                syncParams({ region: e.target.value });
              }}
              aria-label="按地区筛选"
              className="h-9 px-3 text-xs rounded-md border border-background-200 bg-background-50 text-foreground-700 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="">全部地区</option>
              {allRegions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {hasCondition && (
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-sm w-4 h-4 flex items-center justify-center" />
                清除条件
              </button>
            )}
          </div>
        </div>

        <div className="w-full px-4 md:px-8 pb-16">
          <div className="flex items-center gap-2 mb-6 text-sm text-foreground-600">
            {keyword && (
              <span>
                关键词：<strong className="font-semibold text-foreground-900">{keyword}</strong>
              </span>
            )}
            <span className="text-background-400">|</span>
            <span>
              共找到 <strong className="font-semibold text-accent-600">{results.length}</strong> 部作品
            </span>
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {results.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-background-300 rounded-lg">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-3xl text-foreground-400" />
              </span>
              <h3 className="font-heading text-lg font-bold text-foreground-900">没有找到相关影视</h3>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或者返回首页看看最新推荐。</p>
              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={resetAll}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-base w-4 h-4 flex items-center justify-center" />
                  重置条件
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-4-line text-base w-4 h-4 flex items-center justify-center" />
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}